import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/Persons.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/Person.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { Person } from '../../models/Entities/Person/Person';
import {
    CreatePersonCommand,
    PersonFilter,
    UpdatePersonCommand
} from '../../models/Entities/Person/PersonCommands';

export type KnownAction =
    | Actions.Persons.PersonGetAllRequestAction
    | Actions.Persons.PersonGetAllSuccessAction
    | Actions.Persons.PersonGetAllFailureAction
    | Actions.Persons.PersonGetbyIdRequestAction
    | Actions.Persons.PersonGetByIdSuccessAction
    | Actions.Persons.PersonGetByIdFailureAction
    | Actions.Persons.PersonCreateRequestAction
    | Actions.Persons.PersonCreateSuccessAction
    | Actions.Persons.PersonCreateFailureAction
    | Actions.Persons.PersonUpdateRequestAction
    | Actions.Persons.PersonUpdateSuccessAction
    | Actions.Persons.PersonUpdateFailureAction
    | Actions.Persons.PersonDeleteRequestAction
    | Actions.Persons.PersonDeleteSuccessAction
    | Actions.Persons.PersonDeleteFailureAction
    | Actions.Persons.PersonFixIdentifierRequestAction
    | Actions.Persons.PersonFixIdentifierSuccessAction
    | Actions.Persons.PersonFixIdentifierFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const actionCreators = {
    // Without a filter the list is fetched only once, several components rely on
    // this behaviour to populate the person selectors. Passing a filter always
    // refreshes the list.
    GetAllPersons: (filter?: PersonFilter): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        if (filter === undefined && state.Persons?.successLoadingAll) return;

        dispatch({ type: ActionEvents.PERSON_GETALL_REQUEST, filter });

        Services.GetPersons(token, filter)
            .then((response: Person[]) => {
                dispatch({ type: ActionEvents.PERSON_GETALL_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_GETALL_FAILURE, error });
            });
    },

    GetPersonById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_GETBYID_REQUEST, payload: id });

        Services.GetPersonById(token, id)
            .then((response: Person) => {
                dispatch({ type: ActionEvents.PERSON_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_GETBYID_FAILURE, error });
            });
    },

    CreatePerson: (payload: CreatePersonCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_CREATE_REQUEST, payload });

        Services.CreatePerson(token, payload)
            .then((created: Person) => {
                dispatch({ type: ActionEvents.PERSON_CREATE_SUCCESS, payload: created });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_CREATE_FAILURE, error });
            });
    },

    UpdatePerson: (payload: UpdatePersonCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_UPDATE_FAILURE, personId: payload.id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_UPDATE_REQUEST, payload });

        Services.UpdatePerson(token, payload)
            .then((updated: Person) => {
                dispatch({ type: ActionEvents.PERSON_UPDATE_SUCCESS, payload: updated });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_UPDATE_FAILURE, personId: payload.id, error });
            });
    },

    DeletePerson: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_DELETE_FAILURE, personId: id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_DELETE_REQUEST, payload: id });

        Services.DeletePerson(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_DELETE_SUCCESS, payload: id });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_DELETE_FAILURE, personId: id, error });
            });
    },

    FixPersonIdentifiers: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_FIX_IDENTIFIER_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_FIX_IDENTIFIER_REQUEST });

        Services.FixPersonIdentifiers(token)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_FIX_IDENTIFIER_SUCCESS });

                Services.GetPersons(token, state.Persons?.filter)
                    .then((response: Person[]) => {
                        dispatch({ type: ActionEvents.PERSON_GETALL_SUCCESS, payload: response });
                    })
                    .catch((error: ErrorType) => {
                        dispatch({ type: ActionEvents.PERSON_GETALL_FAILURE, error });
                    });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_FIX_IDENTIFIER_FAILURE, error });
            });
    }
};
